import { NavLink, Outlet, useLocation, useNavigate } from "react-router";

import { can, useMe } from "../auth/session";
import { Select } from "../ui/Select";
import { ADMIN_SECTIONS, type AdminSection } from "./sections";
import "./admin.css";

/** Admin pages share a section sidebar (a section picker on narrow screens). */
export function AdminLayout() {
  const me = useMe();
  const groups = ADMIN_SECTIONS.map(
    ([title, sections]) => [title, sections.filter((s) => can(me, s.permission))] as const,
  ).filter(([, sections]) => sections.length > 0);
  return (
    <div className="admin-layout">
      <nav className="admin-sidebar" aria-label="Admin sections">
        {groups.map(([title, sections]) => (
          <div key={title} className="admin-nav-group">
            <p className="admin-nav-title">{title}</p>
            <ul>
              {sections.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink to={to} className="admin-nav-link">
                    <Icon size={18} aria-hidden="true" />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <SectionPicker groups={groups} />
      <div className="admin-main">
        <Outlet />
      </div>
    </div>
  );
}

function SectionPicker({ groups }: { groups: (readonly [string, AdminSection[]])[] }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = groups.flatMap(([, s]) => s).find((s) => pathname.startsWith(s.to))?.to ?? "";
  return (
    <div className="admin-picker">
      <label htmlFor="admin-section" className="visually-hidden">
        Admin section
      </label>
      <Select
        id="admin-section"
        label="Admin section"
        value={current}
        onChange={(to) => navigate(to)}
        groups={groups.map(([title, sections]) => ({ label: title, options: sections.map((s) => ({ value: s.to, label: s.label })) }))}
      />
    </div>
  );
}
